/**
 * nara-set-vs-bucata.js — preţul cel mai bun pentru produsele vândute şi la set, şi la bucată.
 *
 * Problema: nasturii se vând la bucată (13 lei) şi în set de 6 (59 lei). Cine punea 6
 * pe counterul de bucăţi plătea 78 de lei în loc de 59. Iar cine punea 7 plătea 91,
 * când 1 set + 1 bucată costă 72.
 *
 * Ce face: counterul numără MEREU nasturi. Sub el arătăm din ce se compune comanda şi
 * cât costă. La „Adaugă în coş" trimitem liniile corecte: seturile pe câmpul nativ `id`,
 * restul pe `items[0]`.
 *
 * ⚠️ De ce merge trimiterea asta: /cart/add.js procesează `id`/`quantity` ŞI `items[]`
 *    ADITIV, în aceeaşi cerere. Verificat pe magazin. Nu punem seturile în `items[]`,
 *    pentru că product-form.js face `formData.get('id')` şi aruncă „Form ID is required"
 *    dacă lipseşte, deci nu s-ar mai deschide sertarul.
 * ⚠️ Nu facem fetch propriu către /cart/add. Un add-to-cart manual nu deschide sertarul
 *    şi ar ocoli nara-kal-guard.js, care ascultă pe fetch. Aşa, tot fluxul temei rămâne.
 * ⚠️ Detecţia se face pe metafieldul custom.set_size, NU pe numele variantelor:
 *    „Set de 6" e tradus („Set of 6", „6 darabos szett", „Комплект от 6").
 */
(() => {
  'use strict';

  const dataEl = document.getElementById('nara-set-data');
  if (!dataEl) return;

  /** @type {{setSize:number,setId:number,setPrice:number,unitId:number,unitPrice:number,productId:string,moneyProbeA:string}} */
  let D;
  try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!D || !D.setSize || !D.setId || !D.unitId) return;

  const I = window.naraSetI18n || {};
  const LANG = (document.documentElement.lang || 'ro').slice(0, 2).toLowerCase();

  /* ── formatare de bani ─────────────────────────────────────────────────────
     Nu putem folosi `| money` din Liquid pentru o sumă calculată în JS, şi nici
     Intl cu ISO, care ar da „RON" în loc de „lei". Deducem formatul din două
     sonde randate de Liquid pe piaţa CURENTĂ (RON pe .ro, EUR pe pieţele noi). */
  const MONEY = (() => {
    const probe = String(D.moneyProbeA || '');   // 100000 de bani = 1000,00 pe piaţa curentă
    const first = probe.search(/\d/);
    const last = probe.length - 1 - String(probe).split('').reverse().join('').search(/\d/);
    if (first < 0 || last < first) return (cents) => String(Math.round(cents / 100));

    // Tăiem pe POZIŢIA cifrelor, nu pe o clasă de caractere: „1.000,00 lei" are un spaţiu
    // înainte de „lei", iar dacă îl lăsăm în miez ajunge să fie luat drept separator zecimal.
    const core = probe.slice(first, last + 1);   // „1.000,00"
    const prefix = probe.slice(0, first);
    const suffix = probe.slice(last + 1);        // „ lei"

    // Zecimale: sunt exact atunci când al treilea caracter de la coadă e un separator.
    // „1.000,00" → da, virgulă. „1 000" (forint) → nu, e cifră.
    const hasDecimals = core.length >= 3 && /\D/.test(core[core.length - 3]);
    const decSep = hasDecimals ? core[core.length - 3] : '';
    const rest = hasDecimals ? core.slice(0, core.length - 3) : core;
    const restSeps = rest.replace(/\d/g, '');
    const thouSep = restSeps ? restSeps[restSeps.length - 1] : '';

    return (cents) => {
      const neg = cents < 0;
      const v = Math.abs(Math.round(cents));
      const whole = String(hasDecimals ? Math.floor(v / 100) : Math.round(v / 100));
      let out = '';
      for (let i = 0; i < whole.length; i++) {
        if (i > 0 && (whole.length - i) % 3 === 0 && thouSep) out += thouSep;
        out += whole[i];
      }
      if (hasDecimals) out += decSep + String(v % 100).padStart(2, '0');
      return (neg ? '-' : '') + prefix + out + suffix;
    };
  })();

  /* ── plural ────────────────────────────────────────────────────────────────
     Intl.PluralRules ştie regulile fiecărei limbi. Româna are trei forme:
     1 bucată · 2 bucăţi · 20 DE bucăţi. */
  let pr;
  try { pr = new Intl.PluralRules(LANG); } catch (e) { pr = null; }
  const plural = (group, n) => {
    const forms = I[group] || {};
    const cat = pr ? pr.select(n) : (n === 1 ? 'one' : 'other');
    const tpl = forms[cat] || forms.other || forms.one || '[COUNT]';
    return tpl.replace('[COUNT]', String(n)).replace('[SIZE]', String(D.setSize));
  };

  /** Cea mai ieftină descompunere a lui n bucăţi. */
  function split(n) {
    const sets = Math.floor(n / D.setSize);
    const rest = n % D.setSize;
    return { sets, rest, total: sets * D.setPrice + rest * D.unitPrice };
  }

  function describe(n) {
    const { sets, rest } = split(n);
    const parts = [];
    if (sets > 0) parts.push(plural('sets', sets));
    if (rest > 0 || sets === 0) parts.push(plural('pieces', rest || n));
    return parts.join(I.plus || ' + ');
  }

  /* ── legarea de DOM ────────────────────────────────────────────────────────
     ⚠️ Tema face morph pe formular la fiecare schimbare de variantă, deci nodurile
        se înlocuiesc sub noi. De aceea nu ţinem referinţe: le recitim la fiecare
        randare, ascultăm pe `document`, iar linia se re-inserează dacă a fost ştearsă. */

  const FORM_SEL =
    'product-form-component[data-product-id="' + D.productId + '"] form[data-type="add-to-cart-form"]';

  const getForm = () => document.querySelector(FORM_SEL);

  /** Adevărat între trimiterea formularului şi refacerea lui. */
  let swapping = false;

  /** Se aplică doar când e aleasă varianta la bucată şi se strânge cel puţin un set. */
  function state(form) {
    if (!form) return null;
    const idInput = form.querySelector('input[name="id"]');
    // Cât ţine schimbul de la trimitere, câmpul vizibil poartă `nara-qty`,
    // iar `quantity` e cel ascuns cu numărul de seturi. Îl vrem pe cel vizibil.
    const qtyInput =
      form.querySelector('input[name="nara-qty"]') || form.querySelector('input[name="quantity"]');
    if (!idInput || !qtyInput) return null;
    if (String(idInput.value) !== String(D.unitId)) return null;
    const n = parseInt(qtyInput.value, 10);
    if (!Number.isFinite(n) || n < D.setSize) return null;
    const s = split(n);
    if (s.sets < 1) return null;
    return { form, idInput, qtyInput, n, sets: s.sets, rest: s.rest, total: s.total, plain: n * D.unitPrice };
  }

  function render() {
    if (swapping) return;   // în timpul schimbului linia rămâne cum era, fără licărire
    const form = getForm();
    if (!form) return;
    let note = form.querySelector('.nara-set-note');
    const s = state(form);
    if (!s) { if (note && !note.hidden) note.hidden = true; return; }
    if (!note) {
      note = document.createElement('p');
      note.className = 'nara-set-note';
      // ⚠️ Sub TOT rândul de butoane, nu lângă counter: `.product-form-buttons` e flex
      //    pe orizontală, iar linia strecurată înăuntru împingea „Adaugă în coş" în afara ecranului.
      const anchor = form.querySelector('.product-form-buttons') || form.querySelector('.quantity-selector-wrapper');
      if (!anchor || !anchor.parentNode) return;
      anchor.parentNode.insertBefore(note, anchor.nextSibling);
    }
    const saved = s.plain - s.total;
    let txt = describe(s.n) + ' · ' + MONEY(s.total);
    if (saved > 0 && I.save) txt += ' · ' + I.save.replace('[AMOUNT]', MONEY(saved));
    // ⚠️ Scriem DOAR când chiar s-a schimbat ceva. Altfel MutationObserver-ul de mai
    //    jos vede scrierea noastră, cere o nouă randare, care scrie iar: buclă infinită.
    if (note.textContent !== txt) note.textContent = txt;
    if (note.hidden) note.hidden = false;
  }

  const soon = () => setTimeout(render, 0);

  document.addEventListener('input', soon);
  document.addEventListener('change', soon);
  document.addEventListener('click', soon);

  /* Morph-ul temei nu emite niciun eveniment pe care să ne putem baza, deci urmărim
     subarborele formularului şi re-randăm când se schimbă. */
  const host = document.querySelector('product-form-component[data-product-id="' + D.productId + '"]');
  if (host) new MutationObserver(soon).observe(host, { childList: true, subtree: true, attributes: true });

  /* ── trimiterea în coş ─────────────────────────────────────────────────────
     Faza de CAPTURE, pe WINDOW. Nu pe formular, ca legătura să supravieţuiască
     morph-ului temei, şi ⚠️ NU pe `document`: clasa de bază a temei (component.js)
     îşi leagă atributele `on:submit` tot printr-un ascultător delegat pe `document`
     în captare, înregistrat la încărcarea modulului, deci înaintea scriptului nostru.
     Pe acelaşi nod câştigă cine s-a înregistrat primul, iar acela e tema. `window` e
     primul pas din traseul de captare, deci de acolo ajungem înaintea ei.

     ⚠️ Refacerea formularului se face DOAR pe temporizator. Două variante încercate
        înainte au eşuat, amândouă pentru că refăceau prea devreme şi în coş ajungeau
        bucăţile neoptimizate: `setTimeout(..., 0)` şi ascultarea lui `cart:update`.
        Al doilea pare corect, dar `cart:update` e emis de TEMĂ, optimist, înainte să
        construiască payloadul, nu de Shopify după ce răspunde coşul.
     ⚠️ Nu atingem VALOAREA câmpului de cantitate, care e vizibil, ci doar `name`-ul lui:
        aşa clientul vede în continuare numărul de nasturi scris de el, fără licărire.
        Cantitatea reală de seturi pleacă printr-un câmp ascuns. */
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SEL)) return;
    if (swapping) return;
    const s = state(form);
    if (!s) return;

    swapping = true;
    const prevId = s.idInput.value;
    s.idInput.value = String(D.setId);     // seturile pe câmpul nativ `id`
    s.qtyInput.name = 'nara-qty';          // scoatem cantitatea vizibilă din FormData

    const extra = [hidden('quantity', String(s.sets))];
    if (s.rest > 0) {                      // restul pe items[0]: /cart/add le ADUNĂ
      extra.push(hidden('items[0][id]', String(D.unitId)));
      extra.push(hidden('items[0][quantity]', String(s.rest)));
    }
    extra.forEach((el) => form.appendChild(el));

    const restore = () => {
      if (!swapping) return;
      swapping = false;
      s.idInput.value = prevId;
      s.qtyInput.name = 'quantity';
      extra.forEach((el) => el.remove());
      render();
    };
    setTimeout(restore, 5000);   // destul cât tema să-şi construiască payloadul, invizibil pentru client

    /* Bulina din header creşte cu `formData.get('quantity')`, adică doar cu numărul de
       SETURI, şi ar rămâne mai mică decât coşul real. Un `cart:update` cu altă sursă
       decât 'product-form-component' SETEAZĂ numărul în loc să-l adune (cart-icon.js),
       deci îl aducem la adevăr citind coşul. De două ori, ca să prindem şi o reţea lentă. */
    [2000, 5200].forEach((ms) =>
      setTimeout(() => {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                detail: { data: { itemCount: cart.item_count, source: 'nara-set-vs-bucata' } },
              })
            );
          })
          .catch(() => {});
      }, ms)
    );
  }, true);

  function hidden(name, value) {
    const el = document.createElement('input');
    el.type = 'hidden';
    el.name = name;
    el.value = value;
    return el;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render);
  else render();
  setTimeout(render, 1200);   // formularul poate veni dintr-un morph târziu
})();
